export const INPUT_ERROR_CLASS = 'base-input--error'
const PRIVACY_ERROR_CLASS = 'demo-form__privacy--error'
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

const errorFor = (form, name) =>
  form.querySelector(`[data-demo-field-error="${name}"]`)

// The error span's textContent is reused to display context-specific messages
// (invalid email, server-side rejections). Capture the initial required-state
// message once, into a dataset attribute, so it survives later mutations.
const ensureRequiredMessage = errorEl => {
  if (!errorEl.dataset.requiredMessage) {
    errorEl.dataset.requiredMessage = errorEl.textContent
  }
}

const requiredMessageFor = errorEl => errorEl.dataset.requiredMessage

const inputField = (form, input) => {
  const errorEl = errorFor(form, input.name)
  ensureRequiredMessage(errorEl)

  return {
    name: input.name,
    control: input,
    errorTarget: input,
    errorClass: INPUT_ERROR_CLASS,
    errorEl,
    getValue: () => input.value.trim(),
    validate: value => {
      if (!value) return requiredMessageFor(errorEl)
      if (input.type === 'email' && !EMAIL_PATTERN.test(value)) {
        return 'Please enter a valid business email.'
      }
      return null
    }
  }
}

// The phone input is enhanced by intl-tel-input, loaded lazily by ./phone,
// which registers each instance here so the field can read the full number.
const phoneInputs = new WeakMap()

export const registerPhoneInput = (input, iti) => phoneInputs.set(input, iti)

const PHONE_ERROR = 'Please enter a valid phone number.'
const PHONE_CODE_ERROR =
  'Please start the number with your country code, e.g. +1.'
const E164_PATTERN = /^\+\d{7,15}$/

// Without the number utils (not loaded yet, or failed to load) the library
// can't format the number, and the typed text lacks the dial code, which shows
// separately. Prefix the selected country's dial code, dropping a typed trunk
// prefix that starts with 0, which no national number there starts with.
// Without the library itself there is no country to take the code from.
const fallbackPhone = (typed, country) => {
  const hasCode = typed.startsWith('+')
  if (!hasCode && !country) return { number: null, error: PHONE_CODE_ERROR }

  const digits = typed.replace(/\D/g, '')
  const trunk = country?.nationalPrefix
  const national =
    trunk?.startsWith('0') && digits.startsWith(trunk)
      ? digits.slice(trunk.length)
      : digits
  const number = hasCode ? `+${digits}` : `+${country.dialCode}${national}`

  return { number, error: E164_PATTERN.test(number) ? null : PHONE_ERROR }
}

const readPhone = input => {
  const typed = input.value.trim()
  if (!typed) return { number: typed, error: null }

  const iti = phoneInputs.get(input)
  try {
    return {
      number: iti.getNumber('E164'),
      error: iti.isValidNumber() ? null : PHONE_ERROR
    }
  } catch {
    // getNumber throws until the utils load; iti is missing without the library.
    return fallbackPhone(typed, iti?.getSelectedCountry())
  }
}

// Optional: an empty phone passes, a typed one must be valid for its country.
const phoneField = (form, input) => ({
  name: input.name,
  control: input,
  errorTarget: input,
  errorClass: INPUT_ERROR_CLASS,
  errorEl: errorFor(form, input.name),
  getValue: () => readPhone(input).number,
  validate: () => readPhone(input).error
})

const dropdownField = (form, box) => {
  const name = box.dataset.demoMultiselect
  const toggle = box.querySelector('.multiselect__toggle')
  const errorEl = errorFor(form, name)
  ensureRequiredMessage(errorEl)

  return {
    name,
    control: toggle,
    errorTarget: toggle,
    errorClass: INPUT_ERROR_CLASS,
    errorEl,
    getValue: () => box.querySelector('.multiselect__checkbox:checked')?.value,
    validate: value => (value ? null : requiredMessageFor(errorEl))
  }
}

const privacyField = form => {
  const checkbox = form.querySelector('[data-demo-privacy]')
  if (!checkbox) return null
  const errorEl = errorFor(form, 'privacy')
  ensureRequiredMessage(errorEl)

  return {
    name: 'privacy',
    control: checkbox,
    errorTarget: checkbox.closest('.demo-form__privacy'),
    errorClass: PRIVACY_ERROR_CLASS,
    errorEl,
    getValue: () => checkbox.checked,
    validate: value => (value ? null : requiredMessageFor(errorEl))
  }
}

export const buildFields = form => {
  const fields = [
    ...form.querySelectorAll(
      'input[name]:not([type="checkbox"]):not([data-demo-phone]), select[name]'
    )
  ].map(input => inputField(form, input))

  form.querySelectorAll('[data-demo-multiselect]').forEach(box => {
    fields.push(dropdownField(form, box))
  })

  form.querySelectorAll('[data-demo-phone]').forEach(input => {
    fields.push(phoneField(form, input))
  })

  const privacy = privacyField(form)
  if (privacy) fields.push(privacy)

  return fields
}

export const applyFieldState = (field, message) => {
  const invalid = message != null

  field.errorTarget.classList.toggle(field.errorClass, invalid)

  if (invalid) {
    field.control.setAttribute('aria-invalid', 'true')
    field.errorEl.textContent = message
    field.errorEl.classList.remove('hidden')
  } else {
    field.control.removeAttribute('aria-invalid')
    field.errorEl.classList.add('hidden')
  }
}

export const validateField = field => {
  const value = field.getValue()
  const message = field.validate(value)
  applyFieldState(field, message)
  return { invalid: message != null, value }
}

// Resolve the field descriptor a delegated event belongs to. Handles inputs
// and the privacy checkbox directly, plus multiselect checkboxes whose owning
// control is the toggle button on the same box.
export const fieldFor = (fields, target) => {
  const box =
    target && typeof target.closest === 'function'
      ? target.closest('[data-demo-multiselect]')
      : null
  const toggleControl = box?.querySelector('.multiselect__toggle')

  return fields.find(f => f.control === target || f.control === toggleControl)
}
